import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ContextMenu from '../components/ContextMenu'
import ToolsMenu from '../components/ToolsMenu'
import LibraryPanel from '../components/LibraryPanel'
import SemanticSearchPanel from '../components/SemanticSearchPanel'
import GridItemActionsMenu from '../components/GridItemActionsMenu'
import ComparisonLoader from '../components/ComparisonLoader'
import NotificationBell from '../components/NotificationBell'
import NotificationContainer from '../components/NotificationContainer'
import CartFiltersPanel from '../components/CartFiltersPanel'
import LanguageSelector from '../components/LanguageSelector'
import SharedCartAttachment from '../components/SharedCartAttachment'
import { CartsTable } from '../components/Analytics'
import AnalyticsPeriodSlider from '../components/AnalyticsPeriodSlider'
import CumulativeValueChart from '../components/CumulativeValueChart'
import { analyticsSeriesColors } from '../utils/analyticsPolicy'
import '../components/Analytics.css'
import ConversationCartPicker from '../components/ConversationCartPicker'
import SharedCartPreview from '../components/SharedCartPreview'
import { emptyCartFilters } from '../utils/cartFiltersPolicy'
import { NotificationProvider, useNotifications } from '../contexts/NotificationContext'
import './CartInterfacesGallery.css'
import './CompareCartsPage.css'
import './CartDetailPage.css'
import '../components/MessagesPanel.css'

const client = new QueryClient({ defaultOptions: { queries: { retry: false } } })

const CARTS = [
  { id: 'a', name: 'Bureau', total: 128, items_count: 4 },
  { id: 'b', name: 'Courses de la semaine', total: 86.4, items_count: 9 },
  { id: 'c', name: 'Noël', total: 240, items_count: 6 },
  { id: 'd', name: 'Anniversaire de Peyman avec un titre volontairement très long', total: 312.4, items_count: 11 },
  { id: 'e', name: 'Salon', total: 54, items_count: 2 },
  { id: 'f', name: 'Cuisine', total: 77.2, items_count: 5 },
  { id: 'g', name: 'Voyage', total: 890, items_count: 3 },
  { id: 'h', name: 'Rentrée', total: 41, items_count: 8 },
]

const SAVED = [
  { id: 's1', name: 'Lampe de bureau', price: 24, quantity: 1, image_url: '' },
  { id: 's2', name: 'Chaise de conférence au nom volontairement très long', price: 189, quantity: 2, image_url: '' },
  ...Array.from({ length: 10 }, (_, index) => ({
    id: `s${index + 3}`,
    name: `Article enregistré ${index + 3}`,
    price: 12.5 * (index + 1),
    quantity: 1,
    image_url: '',
  })),
]

// Durées allongées : la galerie sert aussi à observer les toasts et leur minuteur
const DEMO_NOTIFICATIONS = [
  {
    type: 'urgent',
    title: 'Budget dépassé',
    message: 'Budget dépassé de 906,91 €. Ajustement requis.',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
    duration: 20000,
  },
  {
    type: 'success',
    title: 'Prix actualisés',
    message: 'Tous les prix du panier sont à jour.',
    timestamp: new Date(Date.now() - 40 * 1000),
    duration: 20000,
  },
  {
    type: 'warning',
    title: 'Budget bientôt atteint',
    message: 'Budget utilisé à 99 %. Il reste 0,40 €.',
    timestamp: new Date(Date.now() - 22 * 60 * 1000),
    duration: 20000,
  },
  {
    type: 'info',
    title: 'Comparaison prête',
    message: 'Les deux paniers sont chargés, la comparaison est disponible.',
    timestamp: new Date(Date.now() - 90 * 1000),
    duration: 20000,
  },
  {
    type: 'info',
    title: 'Bibliothèque',
    message: 'Deux articles enregistrés sont disponibles pour ce panier.',
    timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000),
    showToast: false,
    read: true,
  },
]

function NotificationsMock() {
  const { addNotification, clearAll } = useNotifications()
  const seededRef = useRef(false)

  const seed = useCallback(() => {
    DEMO_NOTIFICATIONS.forEach((notification) => addNotification(notification))
  }, [addNotification])

  useEffect(() => {
    if (seededRef.current) return
    seededRef.current = true
    seed()
  }, [seed])

  return (
    <div className="cart-if-notifications-mock">
      <div className="cart-if-bell-row">
        <NotificationBell />
        <span>Ouvrir le centre de notifications</span>
        <button
          type="button"
          className="cart-if-replay"
          onClick={() => {
            clearAll()
            setTimeout(seed, 60)
          }}
        >
          Rejouer les notifications
        </button>
      </div>
      <NotificationContainer />
    </div>
  )
}

function CartDetailMock({ libraryOpen = true, searchOpen = false, gridMenuOpen = false }) {
  const cardsRef = useRef(null)
  const searchRef = useRef(null)
  const gridButtonRef = useRef(null)
  const [searchExpanded, setSearchExpanded] = useState(searchOpen)
  const [gridAnchor, setGridAnchor] = useState(null)
  const [placement, setPlacement] = useState({ mode: 'beside', workspacePadRight: 0 })
  const handlePlacement = useCallback((next) => {
    setPlacement(next || { mode: 'beside', workspacePadRight: 0 })
  }, [])

  useLayoutEffect(() => {
    if (gridMenuOpen) setGridAnchor(gridButtonRef.current)
  }, [gridMenuOpen])

  return (
    <div
      className={`cart-detail-page cart-if-detail-mock${libraryOpen && placement.mode === 'reflow' ? ' is-library-reflow' : ''}`}
      style={libraryOpen && placement.mode === 'reflow' ? { '--library-workspace-pad': `${placement.workspacePadRight}px` } : undefined}
    >
      <div className="cart-detail-header">
        <div className="header-nav">
          <button type="button" className="btn-icon" title="Retour aux paniers">
            <img src="/Icon house.png" alt="" />
          </button>
        </div>
        <h1 className="cart-name">Panier de démonstration au titre volontairement très long</h1>
        <div className="header-actions">
          <div className={`search-input-wrapper${searchExpanded ? ' is-expanded' : ''}`} ref={searchRef}>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher"
              defaultValue={searchOpen ? 'pc' : ''}
              aria-expanded={searchExpanded}
              onPointerDown={() => setSearchExpanded(true)}
              onFocus={() => setSearchExpanded(true)}
              onBlur={() => { if (!searchOpen) setSearchExpanded(false) }}
            />
          </div>
          <button type="button" className="btn-icon" title="Outils">
            <img src="/Icon construction.png" alt="" />
          </button>
          <button type="button" className="btn-icon" title="Notifications">
            <img src="/Icon inventory.png" alt="" />
          </button>
          <button type="button" className="btn-icon" title="Bibliothèque">
            <img src="/Icon book.png" alt="" />
          </button>
          <button type="button" className="btn-icon" title="Ajouter un produit">
            <img src="/Icon add circle outline.png" alt="" />
          </button>
        </div>
      </div>
      <div className="cart-detail-container" ref={cardsRef}>
        <div className="cart-if-card">Carte produit 1</div>
        <div className="cart-if-card">Carte produit 2</div>
        <div className="cart-if-card">
          Carte produit 3
          <div className="item-buttons grid-actions-menu" style={{ marginTop: 12 }}>
            <button
              ref={gridButtonRef}
              type="button"
              className="btn-icon-small grid-menu-trigger grid-hamburger-container"
              aria-label="Actions"
              aria-expanded={gridMenuOpen}
            >
              <span className={`grid-hamburger-label${gridMenuOpen ? ' is-open is-dir-down' : ''}`} aria-hidden="true">
                <span className="line1" />
                <span className="line2" />
                <span className="line3" />
              </span>
            </button>
          </div>
        </div>
      </div>
      {searchOpen ? (
        <SemanticSearchPanel
          open
          anchorRef={searchRef}
          query="pc"
          items={[
            { id: '1', cart_id: 'demo', name: 'PC Gamer ASUS au nom volontairement très long', price: 1299, cart_name: 'Panier de démonstration', image_url: '' },
            { id: '2', cart_id: 'other', name: 'Ordinateur portable Lenovo', price: 899, cart_name: 'Bureau', image_url: '' },
          ]}
          savedItems={[]}
          currentCartId="demo"
          formatPrice={(price) => `${price} €`}
          onClose={() => {}}
          onSelect={() => {}}
        />
      ) : null}
      {gridMenuOpen ? (
        <GridItemActionsMenu
          isOpen
          anchor={gridAnchor}
          item={{ id: '3', name: 'Carte produit 3', link: 'https://example.com' }}
          onClose={() => {}}
          onAction={() => {}}
          onPlacementChange={() => {}}
        />
      ) : null}
      <LibraryPanel
        isOpen={libraryOpen}
        onClose={() => {}}
        items={SAVED}
        anchorRef={cardsRef}
        onPlacementChange={handlePlacement}
        onAddToCart={() => {}}
        onDelete={() => {}}
      />
    </div>
  )
}

function FeedShareMock() {
  const buttonRef = useRef(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draft, setDraft] = useState('Brouillon conservé pendant la sélection')
  const [selectedCart, setSelectedCart] = useState(null)
  const longName = 'Anniversaire de Peyman avec un titre volontairement très long pour vérifier l’ellipse'
  const filled = {
    id: 'feed-filled',
    name: longName,
    items_count: 3,
    total: 128.4,
    preview_items: [
      { name: 'Lampe', image_url: '' },
      { name: 'Chaise', image_url: '' },
      { name: 'Mug', image_url: '' },
    ],
  }
  return (
    <div style={{ display: 'grid', gap: 24, maxWidth: 560 }}>
      <p className="cart-if-note">Simulation du fil — aucune publication n’est envoyée.</p>
      <div className="create-post-widget" style={{ padding: 16, borderRadius: 16, background: 'rgba(255,255,255,0.06)' }}>
        <textarea className="post-textarea" value={draft} onChange={(event) => setDraft(event.target.value)} rows={3} />
        {selectedCart ? (
          <div className="create-post-attachment">
            <SharedCartAttachment cart={selectedCart} sharedCartId={selectedCart.id} variant="feed" playKey={`draft:${selectedCart.id}`} />
          </div>
        ) : null}
        <div className="post-actions">
          <button ref={buttonRef} type="button" className="action-btn" onClick={() => setPickerOpen((open) => !open)}>
            {selectedCart ? 'Changer le panier' : 'Partager un panier'}
          </button>
          {selectedCart ? (
            <button type="button" className="action-btn" onClick={() => setSelectedCart(null)}>Retirer</button>
          ) : null}
        </div>
        <ConversationCartPicker
          isOpen={pickerOpen}
          anchor={buttonRef.current}
          carts={CARTS}
          onClose={() => setPickerOpen(false)}
          onSelect={(cart) => {
            setSelectedCart({ ...filled, ...cart })
            setPickerOpen(false)
          }}
        />
      </div>
      <article className="post-card" style={{ padding: 16, borderRadius: 16, background: 'rgba(255,255,255,0.06)' }}>
        <p>Regardez ce panier</p>
        <SharedCartAttachment cart={filled} variant="feed" playKey="gallery-feed-filled" />
      </article>
    </div>
  )
}

function StatsMock() {
  const [period, setPeriod] = useState('all')
  const colors = analyticsSeriesColors('dark')
  const series = [
    { date: '10 janv.', cumulative: 100, fullDate: '10 janv. 2026' },
    { date: '1 août', cumulative: 140, fullDate: '1 août 2026' },
    { date: '1 sept.', cumulative: 200, fullDate: '1 sept. 2026' },
  ]
  const rows = [
    { id: 'a', fullName: 'Bureau', total: 100, budget: 80, gap: -20, date: '10 janv. 2026' },
    { id: 'b', fullName: 'Courses', total: 40, budget: 0, gap: null, date: '1 août 2026' },
    { id: '', fullName: 'Panier orphelin', total: 12, budget: 0, gap: null, date: '—' },
  ]
  return (
    <div className="analytics-page" style={{ width: 'min(920px, 100%)' }}>
      <p className="cart-if-note">Simulation des statistiques — aucun panier réel n’est ouvert hors de cette galerie.</p>
      <AnalyticsPeriodSlider value={period} onCommit={setPeriod} />
      <div className="analytics-chart" style={{ marginTop: 24 }}>
        <CumulativeValueChart
          data={period === '30d' ? series.slice(-1) : series}
          height={280}
          colors={colors}
          axis="rgba(247, 247, 248, 0.55)"
          grid="rgba(255, 255, 255, 0.08)"
          animate
          replayToken={period}
        />
      </div>
      <CartsTable rows={rows} persist={false} />
      <div className="lg-panel analytics-modal-panel" style={{ position: 'relative', marginTop: 28, height: 220 }}>
        <div className="analytics-modal">
          <button type="button" className="analytics-modal-close" aria-label="Fermer le graphique">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
            </svg>
          </button>
          <div className="analytics-modal-scroll">
            <header className="analytics-modal-header">
              <h2 id="analytics-modal-title">Valeur cumulée des paniers avec un titre volontairement très long</h2>
            </header>
            <p className="analytics-modal-copy">La croix reste en haut à droite même si le titre déborde.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ConversationShareMock() {
  const buttonRef = useRef(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [anchor, setAnchor] = useState(null)
  useLayoutEffect(() => {
    setAnchor(buttonRef.current)
  }, [])
  const demoCart = {
    id: 'demo-share',
    name: 'Bureau — matériel à tester',
    items_count: 3,
    total: 128.4,
    preview_items: [
      { name: 'Lampe', image_url: '' },
      { name: 'Chaise de conférence au nom volontairement très long', image_url: '' },
      { name: 'Mug', image_url: '' },
    ],
    items: [
      { id: '1', name: 'Lampe de bureau', price: 24, quantity: 2, image_url: '' },
      { id: '2', name: 'Chaise de conférence au nom volontairement très long', price: 80.4, quantity: 1, image_url: '' },
      { id: '3', name: 'Mug', price: 0, quantity: 1 },
    ],
  }
  return (
    <div className="cart-if-share-mock" style={{ padding: 24, display: 'grid', gap: 16, minHeight: '72vh', gridTemplateRows: 'auto 1fr auto' }}>
      <p className="cart-if-note">Simulation locale — aucun message n’est envoyé.</p>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', alignContent: 'end' }}>
        <div className="message-item">
          <div className="message-stack">
            <div className="message-content"><p>Tu peux jeter un œil à ce panier ?</p></div>
            <SharedCartAttachment cart={demoCart} onOpen={() => setPreviewOpen(true)} />
            <SharedCartAttachment
              cart={{ id: 'empty-msg', name: 'Courses du dimanche', items_count: 0, total: 0 }}
            />
            <div className="message-footer"><span className="message-time">À l’instant</span></div>
          </div>
        </div>
        <div className="message-item own">
          <div className="message-stack">
            <SharedCartAttachment cart={{ unavailable: true, id: 'gone' }} sharedCartId="gone" own />
            <div className="message-footer"><span className="message-time">Hier</span><span className="message-status status-sent">✓</span></div>
          </div>
        </div>
      </div>
      <div className="message-form" style={{ maxWidth: 560, alignSelf: 'stretch' }}>
        <div className="message-input-wrapper">
          <input className="message-input" defaultValue="Brouillon conservé" readOnly />
          <button ref={buttonRef} type="button" className="cart-selector-btn" onClick={() => setPickerOpen((open) => !open)}>🛒</button>
        </div>
      </div>
      <ConversationCartPicker
        isOpen={pickerOpen}
        anchor={anchor}
        carts={CARTS}
        onClose={() => setPickerOpen(false)}
        onSelect={() => setPickerOpen(false)}
      />
      <SharedCartPreview
        isOpen={previewOpen}
        cartId="demo-share"
        cartName={demoCart.name}
        demoData={demoCart}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}

function FiltersMock() {
  const [open, setOpen] = useState(true)
  const [filters, setFilters] = useState({ ...emptyCartFilters(), hasImage: 'with', priceMin: '10' })
  const buttonRef = useRef(null)
  return (
    <div className="cart-detail-page cart-if-detail-mock" style={{ minHeight: 520, padding: 24 }}>
      <div className="cart-filters">
        <label ref={buttonRef} className="hamburger-filter has-active">
          <input type="checkbox" checked={open} onChange={(event) => setOpen(event.target.checked)} />
          <svg viewBox="0 0 32 32">
            <path className="line line-top-bottom" d="M27 10 13 10C10.8 10 9 8.2 9 6 9 3.5 10.8 2 13 2 15.2 2 17 3.8 17 6L17 26C17 28.2 18.8 30 21 30 23.2 30 25 28.2 25 26 25 23.8 23.2 22 21 22L7 22" />
            <path className="line" d="M7 16 27 16" />
          </svg>
          <span className="hamburger-filter-count">2</span>
        </label>
        <LanguageSelector />
      </div>
      <div className="cart-summary" style={{ height: 48 }} />
      <div className="cart-detail-container" style={{ marginLeft: 340 }}>
        <div className="cart-if-card">Lampe — 24 €</div>
        <div className="cart-if-card">Mug — 8 €</div>
      </div>
      <CartFiltersPanel
        isOpen={open}
        onClose={() => setOpen(false)}
        anchorRef={buttonRef}
        filters={filters}
        onChange={setFilters}
        onReset={() => setFilters(emptyCartFilters())}
        t={(key) => ({
          advancedFilters: 'Filtres',
          resetFilters: 'Réinitialiser',
          close: 'Fermer',
          priceRange: 'Plage de prix',
          minPrice: 'Prix min',
          maxPrice: 'Prix max',
          hasImage: 'Image',
          hasLink: 'Lien',
          dateAdded: 'Date d’ajout',
          all: 'Tous',
          filterWith: 'Avec',
          filterWithout: 'Sans',
          today: 'Aujourd’hui',
          lastWeek: 'Semaine dernière',
          lastMonth: 'Mois dernier',
          lastYear: 'Année dernière',
        }[key] || key)}
      />
    </div>
  )
}

export default function CartInterfacesGallery() {
  const [screen, setScreen] = useState('menu')
  return (
    <QueryClientProvider client={client}>
     <NotificationProvider>
      <div className="cart-if-gallery">
        <h1>Galerie des interfaces panier</h1>
        <p>Fixtures locales — ce n’est pas une session réelle.</p>
        <nav className="cart-if-nav">
          {[
            ['menu', 'Menu contextuel'],
            ['login', 'Connexion'],
            ['library', 'Bibliothèque'],
            ['search', 'Recherche'],
            ['grid-menu', 'Menu grille'],
            ['filters', 'Filtres'],
            ['share', 'Partage'],
            ['feed', 'Fil d’actualité'],
            ['stats', 'Statistiques'],
            ['tools', 'Outils'],
            ['picker', 'Sélecteur'],
            ['compare', 'Comparaison'],
            ['loading', 'Chargement 3D'],
            ['notifications', 'Notifications'],
          ].map(([id, label]) => (
            <button key={id} type="button" className={screen === id ? 'is-active' : ''} onClick={() => setScreen(id)}>
              {label}
            </button>
          ))}
        </nav>
        {screen === 'menu' ? <ContextMenu x={40} y={120} showCompare onClose={() => {}} onAction={() => {}} /> : null}
        {screen === 'login' ? (
          <p className="cart-if-note">LoginPage n’affiche plus « Télécharger l’extension ». Le bouton reste dans l’espace authentifié.</p>
        ) : null}
        {screen === 'library' ? <CartDetailMock libraryOpen /> : null}
        {screen === 'search' ? <CartDetailMock libraryOpen={false} searchOpen /> : null}
        {screen === 'grid-menu' ? <CartDetailMock libraryOpen={false} gridMenuOpen /> : null}
        {screen === 'filters' ? <FiltersMock /> : null}
        {screen === 'share' ? <ConversationShareMock /> : null}
        {screen === 'feed' ? <FeedShareMock /> : null}
        {screen === 'stats' ? <StatsMock /> : null}
        {screen === 'loading' ? <div className="cart-if-loading-mock"><ComparisonLoader /></div> : null}
        {screen === 'notifications' ? <NotificationsMock /> : null}
        {screen === 'tools' ? (
          <ToolsMenu
            isOpen
            onClose={() => {}}
            onExportJSON={() => {}}
            onExportCSV={() => {}}
            onImport={() => {}}
            onPaste={() => {}}
            onSetBudget={() => {}}
            onShare={() => {}}
            onCompare={() => {}}
            hasBudget
            copiedItemsCount={2}
          />
        ) : null}
        {screen === 'picker' ? (
          <CompareCartPicker isOpen sourceCart={CARTS[0]} carts={CARTS} onClose={() => setScreen('menu')} onSelect={() => setScreen('menu')} />
        ) : null}
        {screen === 'compare' ? (
          <div className="compare-carts-page cart-if-compare-mock">
            <section className="compare-summary">
              <div className="summary-heading">
                <p className="eyebrow">Comparaison</p>
                <button type="button" className="change-carts-link">
                  <img src="/Icon compare.png" alt="" className="compare-picto" />
                  Changer les paniers
                </button>
              </div>
              <div className="summary-grid">
                <div className="summary-cart">
                  <img src="/Icon shopping cart.png" alt="" className="compare-picto" />
                  <h2>Bureau</h2>
                  <strong className="summary-total">108,00 €</strong>
                  <span className="summary-meta">3 références · 3 unités</span>
                </div>
                <div className="summary-cart">
                  <img src="/Icon shopping cart.png" alt="" className="compare-picto" />
                  <h2>Salon</h2>
                  <strong className="summary-total">131,00 €</strong>
                  <span className="summary-meta">3 références · 4 unités</span>
                </div>
              </div>
              <p className="difference-sentence is-higher">
                <img src="/Icon south.png" alt="" className="compare-picto" />
                <span>Salon coûte 23,00 € de plus que Bureau.</span>
              </p>
            </section>
            <details className="compare-help">
              <summary>
                <img src="/Icon data exploration.png" alt="" className="compare-picto" />
                Comment les articles sont-ils comparés ?
              </summary>
              <div className="compare-help-body">
                <p>
                  Deux articles sont associés lorsqu’ils pointent vers la même page produit, une fois le lien nettoyé des paramètres publicitaires.
                  Le nom seul ne suffit jamais à les considérer comme identiques.
                </p>
                <label className="fallback-toggle">
                  <input type="checkbox" />
                  <span>
                    Associer aussi les articles qui partagent exactement la même image externe.
                    Une image identique n’est pas une preuve suffisante qu’il s’agit du même produit.
                  </span>
                </label>
              </div>
            </details>
            <nav className="comparison-views">
              <button type="button" className="is-active">
                <img src="/Icon view list.png" alt="" className="compare-picto" />Tous<span>4</span>
              </button>
              <button type="button">
                <img src="/Icon compare.png" alt="" className="compare-picto" />Communs<span>2</span>
              </button>
              <button type="button">
                <img src="/Icon shopping cart.png" alt="" className="compare-picto" />Uniquement A<span>1</span>
              </button>
              <button type="button">
                <img src="/Icon shopping cart.png" alt="" className="compare-picto" />Uniquement B<span>1</span>
              </button>
            </nav>
            <section className="comparison-list">
              <div className="comparison-row-wrap">
                <article className="comparison-row row-common">
                  <div className="comparison-side">
                    <span className="side-label">Panier A</span>
                    <div className="product-heading">
                      <div className="comparison-missing">
                        <img src="/Icon photo size select actual.png" alt="" className="compare-picto" />
                        <span>Image indisponible</span>
                      </div>
                      <h3>Lampe de bureau noire</h3>
                    </div>
                    <dl className="product-metrics">
                      <div className="is-diff"><dt>Prix unitaire</dt><dd>20,00 €</dd></div>
                      <div className="is-diff"><dt>Quantité</dt><dd>1</dd></div>
                      <div><dt>Sous-total</dt><dd>20,00 €</dd></div>
                    </dl>
                  </div>
                  <div className="row-status">
                    <img src="/Icon south.png" alt="" className="compare-picto" />
                    <span>Prix et quantité</span>
                  </div>
                  <div className="comparison-side">
                    <span className="side-label">Panier B</span>
                    <div className="product-heading">
                      <div className="comparison-missing">
                        <img src="/Icon photo size select actual.png" alt="" className="compare-picto" />
                        <span>Image indisponible</span>
                      </div>
                      <h3>Lampe LED</h3>
                    </div>
                    <dl className="product-metrics">
                      <div className="is-diff"><dt>Prix unitaire</dt><dd>24,00 €</dd></div>
                      <div className="is-diff"><dt>Quantité</dt><dd>2</dd></div>
                      <div><dt>Sous-total</dt><dd>48,00 €</dd></div>
                    </dl>
                  </div>
                </article>
              </div>
            </section>
          </div>
        ) : null}
      </div>
     </NotificationProvider>
    </QueryClientProvider>
  )
}
